<script setup lang="ts">
import { ref } from 'vue'
import axios from 'axios'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'
import { useViewStore } from '@/stores/viewStore'
import { get_data_attr } from '@/utils/globals'

const { toast } = useToast()
const viewStore = useViewStore()

const customerInfo = get_data_attr('customer')
const customers = get_data_attr('customers')

const isSubmitting = ref(false)
const gbuBatchFile = ref<File | null>(null)

const form = ref({
  datasheet: { name_responsible: '', email_responsible: '', email_department: '', next_check: '' },
  gbu: { name_responsible: '', email_responsible: '', email_department: '', next_check: '' },
  substitutiontest: { name_responsible: '', email_responsible: '', email_department: '', next_check: '' },
  betriebsanweisung: { name_responsible: '', email_responsible: '', email_department: '', next_check: '' },
})

const sectionConfig = [
  { key: 'datasheet', label: 'Sicherheitsdatenblaetter' },
  { key: 'gbu', label: 'GBU' },
  { key: 'substitutiontest', label: 'Substitutionstests' },
  { key: 'betriebsanweisung', label: 'Betriebsanweisungen' },
] as const

function customerLabel() {
  const customerId = customerInfo?.id
  const match = customers?.find((c: any) => String(c.value) === String(customerId))
  const name = match?.label || customerInfo?.Kunde || customerInfo?.name || 'Unbekannt'
  return `${name} (ID = ${customerId ?? '-'})`
}

function backToList() {
  viewStore.setActionView('registry.gefahrstoffe')
}

function onGbuBatchFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  gbuBatchFile.value = input.files?.[0] ?? null
}

function isBlankContactValue(v: unknown): boolean {
  return v == null || String(v).trim() === ''
}

function buildBatchUpdateFormData() {
  const fd = new FormData()

  // Only send non-blank contact fields so empty inputs never overwrite existing data (server also uses compact_blank).
  const scopes = ['datasheet', 'gbu', 'substitutiontest', 'betriebsanweisung'] as const
  for (const scope of scopes) {
    const row = form.value[scope]
    for (const [k, v] of Object.entries(row)) {
      if (!isBlankContactValue(v)) {
        fd.append(`${scope}[${k}]`, String(v).trim())
      }
    }
  }

  if (gbuBatchFile.value) {
    fd.append('gbu_file', gbuBatchFile.value)
  }

  return fd
}

async function submitBatchUpdate() {
  isSubmitting.value = true
  try {
    const res = await axios.post('/gefahrstoffe/batch_update_contacts', buildBatchUpdateFormData())
    const data = res.data || {}
    const gbuFilePart =
      (data.gbu_batch_file_applied || 0) > 0
        ? ` GBU-Datei für ${data.gbu_batch_file_applied} Gefahrstoff(e) (gemeinsame Datei).`
        : ''
    toast({
      title: 'Batch-Update erfolgreich',
      description: `${data.updated_gefahrstoffe || 0} Gefahrstoffe gefiltert (${data.updated_nested_records || 0} Kontakt-Eintraege geaendert, ${data.created_nested_records || 0} neu angelegt).${gbuFilePart}`,
    })

    // Go back to the list and force remount so the table reloads.
    viewStore.forceRefresh()
    viewStore.setActionView('registry.gefahrstoffe')
  } catch (error: any) {
    const message =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      'Batch-Update fehlgeschlagen.'
    toast({ title: 'Fehler', description: message, variant: 'destructive' })
  } finally {
    isSubmitting.value = false
  }
}

</script>

<template>
  <div class="p-4">
    <Card class="p-6 space-y-6 max-w-5xl">
      <div class="flex items-center justify-between">
        <h2 class="text-xl font-bold">Gefahrstoffe Batch-Update</h2>
        <Button variant="outline" @click="backToList">Zurück</Button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="text-sm font-semibold">Ausgewählter Kunde</label>
          <div class="mt-1 p-2 border rounded bg-gray-50">{{ customerLabel() }}</div>
        </div>
      </div>

      <div v-for="section in sectionConfig" :key="section.key" class="space-y-2 border rounded p-4">
        <h3 class="font-semibold">{{ section.label }}</h3>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label class="text-sm">Verantwortlicher</label>
            <Input v-model="form[section.key].name_responsible" />
          </div>
          <div>
            <label class="text-sm">Verantwortlicher E-Mail</label>
            <Input v-model="form[section.key].email_responsible" type="email" />
          </div>
          <div>
            <label class="text-sm">Email Abteilung</label>
            <Input v-model="form[section.key].email_department" type="email" />
          </div>
          <div>
            <label class="text-sm">Nächste Prüfung</label>
            <Input v-model="form[section.key].next_check" type="date" />
          </div>
        </div>
        <div v-if="section.key === 'gbu'" class="pt-2 border-t mt-3">
          <label class="text-sm font-medium" for="gbu_batch_file">GBU-Datei (Batch)</label>
          <p class="text-xs text-muted-foreground mt-1 mb-2">
            Optional: eine Datei für alle gefilterten Gefahrstoffe; dieselbe Datei wird in der Datenbank
            gemeinsam referenziert.
          </p>
          <input
            id="gbu_batch_file"
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx"
            class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm cursor-pointer file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            @change="onGbuBatchFileChange"
          />
          <p v-if="gbuBatchFile" class="text-sm text-green-700 mt-2">
            Ausgewählt: {{ gbuBatchFile.name }}
          </p>
        </div>
      </div>

      <div class="flex justify-end">
        <Button class="greenbutton text-white" :disabled="isSubmitting" @click="submitBatchUpdate">
          {{ isSubmitting ? 'Speichere...' : 'Submit' }}
        </Button>
      </div>
    </Card>
  </div>
</template>
